import React, { useState, useRef, useEffect } from 'react';
import { useField } from 'formik';
import { IoChevronDownSharp } from 'react-icons/io5';
import { TbAlertTriangleFilled } from 'react-icons/tb';
import classNames from 'classnames';

interface FormikSelectFieldProps {
  label: string;
  name: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const FormikSelectField: React.FC<FormikSelectFieldProps> = ({
  label,
  name,
  options,
  placeholder = 'Select...',
  disabled = false,
  className = ''
}) => {
  const [field, meta, helpers] = useField(name);
  const { touched, error } = meta;
  const { setValue, setTouched } = helpers;
  const showError = Boolean(touched && error);
  const selectId = `${name}-select`;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (value: string) => {
    setValue(value);
    setTouched(true);
    setIsOpen(false);
  };

  const selectedOption = options.find((option) => option.value === field.value);

  return (
    <div className={`w-full ${className}`}>
      <label
        htmlFor={selectId}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>
      <div className="relative" ref={dropdownRef}>
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={classNames(
            'mt-1 relative flex items-center w-full px-4 py-3 text-base border focus:outline-none focus:ring-indigo-500 sm:text-sm rounded-md cursor-pointer',
            showError
              ? 'border-red-800 focus:border-red-800'
              : 'border-gray-300 focus:border-indigo-500',
            disabled ? 'bg-gray-100 cursor-not-allowed' : '',
            field.value ? 'text-gray-900' : 'text-gray-500'
          )}
        >
          <div className="flex flex-1">
            {selectedOption ? selectedOption.label : placeholder}
          </div>
          <div className="flex items-center pointer-events-none">
            <IoChevronDownSharp
              className={`text-gray-400 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </div>
        </div>

        {isOpen && !disabled && (
          <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {options.length === 0 ? (
              <li className="text-gray-500 select-none relative py-3 pl-4 pr-9">
                No options available
              </li>
            ) : (
              options.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={classNames(
                    'cursor-pointer select-none relative py-3 pl-4 pr-9',
                    option.value === field.value
                      ? 'bg-indigo-100 text-indigo-900'
                      : 'text-gray-900 hover:bg-gray-100'
                  )}
                >
                  <div className="flex items-center">
                    <span className="flex flex-1">{option.label}</span>
                    {option.value === field.value && (
                      <span className="text-indigo-600 absolute inset-y-0 right-0 flex items-center pr-4">
                        <svg
                          className="h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        )}

        {/* Hidden select for form submission */}
        <select
          id={selectId}
          name={field.name}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          className="sr-only"
          aria-hidden="true"
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {showError && (
        <div className="mt-2 text-red-800 flex items-center gap-1 font-normal text-sm">
          <TbAlertTriangleFilled size={16} /> {error}
        </div>
      )}
    </div>
  );
};

export default FormikSelectField;
